import { useState, useEffect, useCallback } from 'react';
import type { Lead, LeadStatus, LeadSource } from '../types';
import * as api from '../api';

export interface LeadFilters {
  status?: LeadStatus;
  source?: LeadSource;
  assignee?: string;
}

export interface LeadSortConfig {
  field: 'name' | 'email' | 'source' | 'status' | 'score' | 'assigned_to' | 'created_at';
  direction: 'asc' | 'desc';
}

interface UseLeadsOptions {
  filters?: LeadFilters;
  sort?: LeadSortConfig;
  pageSize?: number;
}

interface UseLeadsResult {
  leads: Lead[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
  setPage: (page: number) => void;
  refreshData: () => Promise<void>;
  updateLead: (leadId: string, updates: api.UpdateLeadRequest) => Promise<Lead | null>;
  deleteLead: (leadId: string) => Promise<boolean>;
  qualifyLead: (leadId: string) => Promise<Lead | null>;
  unqualifyLead: (leadId: string) => Promise<Lead | null>;
  convertLead: (leadId: string, request: api.ConvertLeadRequest) => Promise<api.ConvertLeadResponse | null>;
}

export function useLeads(options: UseLeadsOptions = {}): UseLeadsResult {
  const { filters, sort, pageSize = 20 } = options;
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: -1,
    page: 1,
    pageSize,
    hasMore: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.fetchLeads({
        status: filters?.status,
        source: filters?.source,
        assignee: filters?.assignee,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      let sortedLeads = [...response.data];

      // Client-side sorting (API may not support all sort options)
      if (sort) {
        sortedLeads.sort((a, b) => {
          let aVal: string | number;
          let bVal: string | number;

          switch (sort.field) {
            case 'name':
              // Use contact_id or metadata.name if available
              aVal = a.metadata?.name || a.contact_id || '';
              bVal = b.metadata?.name || b.contact_id || '';
              break;
            case 'email':
              aVal = a.metadata?.email || '';
              bVal = b.metadata?.email || '';
              break;
            case 'source':
              aVal = a.source;
              bVal = b.source;
              break;
            case 'status':
              aVal = a.status;
              bVal = b.status;
              break;
            case 'score':
              aVal = a.score;
              bVal = b.score;
              break;
            case 'assigned_to':
              aVal = a.assigned_to || '';
              bVal = b.assigned_to || '';
              break;
            case 'created_at':
              aVal = new Date(a.created_at).getTime();
              bVal = new Date(b.created_at).getTime();
              break;
            default:
              aVal = '';
              bVal = '';
          }

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
          }

          const comparison = String(aVal).localeCompare(String(bVal));
          return sort.direction === 'asc' ? comparison : -comparison;
        });
      }

      setLeads(sortedLeads);
      setPagination({
        total: response.pagination.total,
        page,
        pageSize,
        hasMore: response.pagination.has_more,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, pageSize]);

  // Update lead
  const updateLeadHandler = useCallback(async (
    leadId: string,
    updates: api.UpdateLeadRequest
  ): Promise<Lead | null> => {
    try {
      const updated = await api.updateLead(leadId, updates);
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lead');
      return null;
    }
  }, []);

  // Delete lead
  const deleteLeadHandler = useCallback(async (leadId: string): Promise<boolean> => {
    try {
      await api.deleteLead(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete lead');
      return false;
    }
  }, []);

  // Qualify lead
  const qualifyLeadHandler = useCallback(async (leadId: string): Promise<Lead | null> => {
    try {
      const updated = await api.qualifyLead(leadId);
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to qualify lead');
      return null;
    }
  }, []);

  // Unqualify lead
  const unqualifyLeadHandler = useCallback(async (leadId: string): Promise<Lead | null> => {
    try {
      const updated = await api.unqualifyLead(leadId);
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unqualify lead');
      return null;
    }
  }, []);

  // Convert lead
  const convertLeadHandler = useCallback(async (
    leadId: string,
    request: api.ConvertLeadRequest
  ): Promise<api.ConvertLeadResponse | null> => {
    try {
      const result = await api.convertLead(leadId, request);
      setLeads(prev => prev.map(l => l.id === leadId ? result.lead : l));
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert lead');
      return null;
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters?.status, filters?.source, filters?.assignee]);

  return {
    leads,
    loading,
    error,
    pagination,
    setPage,
    refreshData: loadData,
    updateLead: updateLeadHandler,
    deleteLead: deleteLeadHandler,
    qualifyLead: qualifyLeadHandler,
    unqualifyLead: unqualifyLeadHandler,
    convertLead: convertLeadHandler,
  };
}

// Hook for single lead detail
interface UseLeadDetailResult {
  lead: Lead | null;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  updateLead: (updates: api.UpdateLeadRequest) => Promise<Lead | null>;
  deleteLead: () => Promise<boolean>;
  qualifyLead: () => Promise<Lead | null>;
  unqualifyLead: () => Promise<Lead | null>;
  convertLead: (request: api.ConvertLeadRequest) => Promise<api.ConvertLeadResponse | null>;
}

export function useLeadDetail(leadId: string): UseLeadDetailResult {
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!leadId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await api.fetchLead(leadId);
      setLead(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const updateLeadHandler = useCallback(async (
    updates: api.UpdateLeadRequest
  ): Promise<Lead | null> => {
    if (!leadId) return null;
    try {
      const updated = await api.updateLead(leadId, updates);
      setLead(updated);
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lead');
      return null;
    }
  }, [leadId]);

  const deleteLeadHandler = useCallback(async (): Promise<boolean> => {
    if (!leadId) return false;
    try {
      await api.deleteLead(leadId);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete lead');
      return false;
    }
  }, [leadId]);

  const qualifyLeadHandler = useCallback(async (): Promise<Lead | null> => {
    if (!leadId) return null;
    try {
      const updated = await api.qualifyLead(leadId);
      setLead(updated);
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to qualify lead');
      return null;
    }
  }, [leadId]);

  const unqualifyLeadHandler = useCallback(async (): Promise<Lead | null> => {
    if (!leadId) return null;
    try {
      const updated = await api.unqualifyLead(leadId);
      setLead(updated);
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unqualify lead');
      return null;
    }
  }, [leadId]);

  const convertLeadHandler = useCallback(async (
    request: api.ConvertLeadRequest
  ): Promise<api.ConvertLeadResponse | null> => {
    if (!leadId) return null;
    try {
      const result = await api.convertLead(leadId, request);
      setLead(result.lead);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert lead');
      return null;
    }
  }, [leadId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    lead,
    loading,
    error,
    refreshData: loadData,
    updateLead: updateLeadHandler,
    deleteLead: deleteLeadHandler,
    qualifyLead: qualifyLeadHandler,
    unqualifyLead: unqualifyLeadHandler,
    convertLead: convertLeadHandler,
  };
}

// Helper functions
export const statusColors: Record<LeadStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-green-100 text-green-700',
  unqualified: 'bg-gray-100 text-gray-500',
  converted: 'bg-purple-100 text-purple-700',
};

export const statusLabels: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
  converted: 'Converted',
};

export const sourceLabels: Record<LeadSource, string> = {
  web: 'Website',
  referral: 'Referral',
  dsleadgen: 'DS Lead Gen',
  manual: 'Manual',
  import: 'Import',
  api: 'API',
};

export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
}

export function formatLeadDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
